/* eslint-disable react-hooks/exhaustive-deps */
import {
    CheckCircle as CheckCircleIcon,
    CheckCircleOutline as CheckCircleOutlineIcon,
    Error as ErrorIcon,
    ErrorOutline as ErrorOutLineIcon,
} from '@mui/icons-material';
import { Grid, IconButton, TextField, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import {
    createComponentValue as createComponentValueAction,
    fetchMaintenanceIssue as fetchMaintenanceIssueAction,
} from '../../../../../../../../actions/collections/maintenanceIssues';
import { reactFinalFormHOC } from '../../../../../../../../form';

const useStyles = makeStyles(theme => ({
    completedTitle: {
        opacity: 0.38,
    },
    item: {
        border: `1px solid ${theme.isDark() ? 'rgba(255, 255, 255, 0.38)' : 'rgba(0, 0, 0, 0.38)'}`,
        borderRadius: 4,
        margin: theme.spacing(0.5),
        padding: theme.spacing(0, 2),
    },
    itemCompleted: {
        border: `1px solid rgba(0, 0, 0, 0.12)`,
    },
    nested: {
        paddingLeft: theme.spacing(0.5),
    },
    warningButton: {
        color: theme.palette.warning.main,
    },
}));

function ChecklistComponent({
    disabled,
    fetchMaintenanceIssue,
    id,
    data,
    issueId,
    itemId,
    initialValues,
    createComponentValue,
}) {
    const classes = useStyles();
    const { formatMessage } = useIntl();

    const [formData, setFormData] = useState(initialValues);
    const { hasProblems, completed } = formData;

    const initialRender = useRef(true);
    useEffect(() => {
        (async () => {
            if (initialRender.current) {
                initialRender.current = false;
            } else if (formData) {
                await createComponentValue({
                    body: formData,
                    issueId,
                    itemId,
                });
                fetchMaintenanceIssue({ id: issueId });
            }
        })();
    }, [createComponentValue, hasProblems, completed, issueId, itemId, formData]);

    const handleChange = (name, value) => {
        if (formData[name] !== value) {
            setFormData(prevState => ({
                ...prevState,
                [name]: value,
            }));
        }
    };

    return (
        <Grid
            alignItems="center"
            className={classNames(classes.item, completed && classes.itemCompleted)}
            container
            spacing={2}
        >
            <Grid container item spacing={2} xs>
                <Grid item xs={12}>
                    <Typography
                        className={completed ? classes.completedTitle : ''}
                        id={`${id}Title`}
                        variant="body1"
                    >
                        {data.title}
                    </Typography>
                </Grid>
                {hasProblems && (
                    <Grid item xs={12}>
                        <TextField
                            defaultValue={formData.value}
                            disabled={disabled}
                            fullWidth
                            id={`${id}Comment`}
                            label={formatMessage({
                                defaultMessage: 'Describe the problem',
                                id: 'task.maintenance.describeProblem',
                            })}
                            name="value"
                            onBlur={({ target }) => handleChange('value', target.value)}
                            onKeyPress={({ key, target }) => key === 'Enter' && target.blur()}
                        />
                    </Grid>
                )}
            </Grid>
            <Grid item xs="auto">
                <IconButton
                    disabled={disabled}
                    onClick={() => {
                        handleChange('hasProblems', !hasProblems);
                        handleChange('completed', !hasProblems);
                    }}
                    size="large"
                >
                    {completed && hasProblems ? (
                        <ErrorIcon className={classes.warningButton} />
                    ) : (
                        <ErrorOutLineIcon />
                    )}
                </IconButton>
                <IconButton
                    disabled={disabled}
                    onClick={() => {
                        handleChange('hasProblems', false);
                        handleChange('completed', hasProblems || !completed);
                    }}
                    size="large"
                >
                    {completed && !hasProblems ? (
                        <CheckCircleIcon color="secondary" />
                    ) : (
                        <CheckCircleOutlineIcon />
                    )}
                </IconButton>
            </Grid>
        </Grid>
    );
}

const ComponentForm = reactFinalFormHOC()(ChecklistComponent);

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            createComponentValue: createComponentValueAction,
            fetchMaintenanceIssue: fetchMaintenanceIssueAction,
        },
        dispatch
    );

const mapStateToProps = (state, { data }) => ({
    initialValues: {
        completed: data.completed,
        componentId: data.componentType === 'CHECKBOX' && data.id,
        hasProblems: data.hasProblems,
        value: data.value,
    },
});

const ConnectedComponentForm = connect(mapStateToProps, mapDispatchToProps)(ComponentForm);

export default ConnectedComponentForm;
